import "./PriceDisplay.scss";

import React from "react";
import { usePriceData } from "../../hooks/usePriceData";
import { InternalDisplayConfig } from "shared/src/types/ArticleCardProps";
import { PriceAndPromotionInfo } from "shared/src/types/PriceAndPromotionInfo";
import { getRelativePathForRedirectAfterLogin } from "../../utils/PriceDisplayFramework";
import { FormattedNumber, FormattedMessage } from "react-intl";
import MoneyAmount from "shared/src/components/PriceDisplay/MoneyAmount";
import LinkContainer from "../display/LinkContainer";
import NoPrice from "./NoPrice";

type Props = {
    priceAndPromotionInfo: PriceAndPromotionInfo | null;
    internalDisplayConfig: InternalDisplayConfig;
    isWeightedArticleRaw: "WEIGHT" | "NON_WEIGHT" | undefined;
    bettyBundleId: string;
    isSingletonBundle: boolean;
    isLeadingArticle: boolean;
};

function PriceDisplay({
    priceAndPromotionInfo,
    internalDisplayConfig,
    isWeightedArticleRaw,
    bettyBundleId,
    isSingletonBundle,
    isLeadingArticle
}: Props) {
    const { priceData, isWeightArticle } = usePriceData(
        priceAndPromotionInfo,
        internalDisplayConfig,
        isWeightedArticleRaw
    );
    const locationSearch: string | undefined = internalDisplayConfig?.location?.search;
    const pathName = getRelativePathForRedirectAfterLogin(internalDisplayConfig?.location);
    const isLoggedIn = internalDisplayConfig?.isLoggedIn;
    const customerTier = internalDisplayConfig?.customerTier;

    // JPY currency needs 0 decimal places instead of 2
    const format = priceData?.currency === "JPY" ? "currencyJPY" : "currency";
    if (!priceData?.primary) {
        // If possible to show reason why price isn't shown
        if (priceData?.priceHiddenReason === "ANON_CUSTOMER_VIEWING_FOOD_ARTICLE") {
            return (
                <div className="price-display">
                    <p className="price primary ex-price-anon">-,-</p>
                    <LinkContainer to={{ pathname: pathName, search: locationSearch }}>
                        <FormattedMessage id="EX_NO_PRICE_AS_ANONYM_MSG" />
                    </LinkContainer>
                </div>
            );
        } else if (priceData?.priceHiddenReason === "NONFOOD_CUSTOMER_VIEWING_FOOD_ARTICLE") {
            return (
                <div role="region" aria-label="Price status">
                    <p className="price primary ex-price-status-non-food">-,-</p>
                    <FormattedMessage
                        className="price primary ex-price-status-non-food"
                        id="EX_NO_PRICE_CONTRACT_ONLY_MSG"
                    />
                    <br />
                    <LinkContainer to={{ pathname: pathName, search: locationSearch }}>
                        <FormattedMessage id="EX_NO_PRICE_LOGIN_MSG" />
                    </LinkContainer>
                </div>
            );
        } else if (priceData?.priceHiddenReason === "STATUS_20_NOSTOCK_ARTICLE") {
            return (
                <div role="region" aria-label="Price status">
                    <p className="price primary ex-price-status-20">-,-</p>
                    <FormattedMessage
                        className="price primary ex-price-status-20"
                        id="EX_PRICE_NO_LONGER_AVAILABLE_MSG"
                    />
                </div>
            );
        }
        if (!priceData?.secondary) {
            if (isLoggedIn && !(customerTier === "1" || customerTier === "2"))
                // 2022-01 don't show "noprice" for T1/T2 as this is probably no error, we currently never get them.
                // TODO remove this as soon as we get prices for T1/T2.
                return (
                    <div className="no-price-available">
                        <NoPrice id={"noprice" + bettyBundleId} />
                    </div>
                );
            else if (!isLoggedIn && !priceData?.priceHiddenReason)
                return (
                    <LinkContainer to={{ pathname: pathName, search: locationSearch }}>
                        <FormattedMessage id="EX_NO_PRICE_AS_ANONYM_MSG" />
                    </LinkContainer>
                );
        }
    }
    if (!priceData)
        // This should normally not happen, but if so, we cannot display anything
        return null;

    return (
        <div className="price-display">
            <div className="additional-price-info-row">
                {isWeightArticle ? (
                    <FormattedMessage id="SD_PRICE_PER_KG" />
                ) : (
                    priceData.piece &&
                    !isSingletonBundle &&
                    // workaround - see ArticleCard and BTP-HV3QO5
                    !isLeadingArticle && (
                        <>
                            <FormattedNumber
                                value={priceData.piece.price}
                                format={format}
                                currency={priceData.currency}
                            />
                            &nbsp;/&nbsp;
                            <FormattedMessage id="SD_PRICE_PIECE" />
                        </>
                    )
                )}
            </div>
            <div className="price-display-main-row">
                {priceData.primary && (
                    <MoneyAmount
                        price={priceData?.primary.price}
                        currency={priceData.currency}
                        isWeightArticle={undefined}
                        unitIndicatorWeight={undefined}
                        messageTranslationKey={priceData?.primary.info}
                        className={"primary " + priceData.primary.style}
                    />
                )}
                {priceData.primary?.strikeThrough && (
                    <span className="strike-through">
                        <FormattedNumber
                            value={priceData?.primary?.strikeThrough}
                            format={format}
                            currency={priceData.currency}
                        />
                    </span>
                )}
                {priceData.primary?.style === "cip" && (
                    <span className="cip-icon">
                        <span className="mfcss_article-detail--sticker-container">
                            <span className="icon-small-cip text-primary mfcss_article-detail--sticker-icon"></span>
                        </span>
                    </span>
                )}
            </div>
            <div className="secondary-price-row">
                {priceData?.secondary && (
                    <MoneyAmount
                        price={priceData?.secondary?.price}
                        currency={priceData.currency}
                        isWeightArticle={undefined}
                        unitIndicatorWeight={undefined}
                        messageTranslationKey={priceData?.secondary?.info}
                        className="secondary"
                    />
                )}
            </div>
        </div>
    );
}

export default PriceDisplay;
